import React from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { Text } from '../../components/ui'
import type { ComercialActivity } from '../../types/comercialAtividades'
import { FEEDBACK_LABELS } from '../../types/comercialAtividades'

const COLORS = ['#22c55e', '#6b7280', '#e11d48', '#008B95', '#f97316', '#6366f1', '#14b8a6']

interface AtividadesDashboardTabProps {
  activities: ComercialActivity[]
}

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
}

const AtividadesDashboardTab: React.FC<AtividadesDashboardTabProps> = ({ activities }) => {
  const totalAtividades = activities.length
  const totalPositivo = React.useMemo(
    () => activities.filter((a) => a.overall_feedback === 'positivo').length,
    [activities],
  )
  const totalNegativo = React.useMemo(
    () => activities.filter((a) => a.overall_feedback === 'negativo').length,
    [activities],
  )

  const feedbackChartData = React.useMemo(
    () => [
      { name: FEEDBACK_LABELS.positivo, value: activities.filter((a) => a.overall_feedback === 'positivo').length },
      { name: FEEDBACK_LABELS.neutro, value: activities.filter((a) => a.overall_feedback === 'neutro').length },
      { name: FEEDBACK_LABELS.negativo, value: activities.filter((a) => a.overall_feedback === 'negativo').length },
    ],
    [activities],
  )

  const byTypeChartData = React.useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    activities.forEach((a) => {
      const name = a.activity_type?.name ?? 'Sem tipo'
      const current = map.get(name) ?? { name, count: 0 }
      current.count += 1
      map.set(name, current)
    })
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [activities])

  const byMonthChartData = React.useMemo(() => {
    const now = new Date()
    const months: { key: string; name: string; count: number }[] = []
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      months.push({ key, name: formatMonthLabel(key), count: 0 })
    }
    const monthMap = new Map(months.map((m) => [m.key, m]))
    activities.forEach((a) => {
      const key = a.activity_date.slice(0, 7)
      const entry = monthMap.get(key)
      if (entry) entry.count += 1
    })
    return months
  }, [activities])

  const topCorretores = React.useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    activities.forEach((a) => {
      // Ranking só com atividades vinculadas a corretor cadastrado
      if (!a.broker_id || !a.broker?.name) return
      const scgp = a.broker.id_scgp != null ? ` (${a.broker.id_scgp})` : ''
      const label = `${a.broker.name}${scgp}`
      const current = map.get(label) ?? { name: label, count: 0 }
      current.count += 1
      map.set(label, current)
    })
    return Array.from(map.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [activities])

  return (
    <div className="px-6 py-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-4 text-white">
          <div className="text-center">
            <Text size="2xl" className="text-white font-bold mb-1">
              {totalAtividades}
            </Text>
            <Text size="sm" className="text-white">
              Total de atividades
            </Text>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-4 text-white">
          <div className="text-center">
            <Text size="2xl" className="text-white font-bold mb-1">
              {totalPositivo}
            </Text>
            <Text size="sm" className="text-white">
              Feedback positivo
            </Text>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-4 text-white">
          <div className="text-center">
            <Text size="2xl" className="text-white font-bold mb-1">
              {totalNegativo}
            </Text>
            <Text size="sm" className="text-white">
              Feedback negativo
            </Text>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Distribuição de feedback
            </Text>
            <Text size="sm" color="muted">
              Positivo / Neutro / Negativo
            </Text>
          </div>
          <div className="h-64">
            {totalAtividades === 0 ? (
              <div className="h-full flex items-center justify-center">
                <Text size="sm" color="muted">
                  Nenhuma atividade registrada ainda.
                </Text>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={feedbackChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {feedbackChartData.map((entry, index) => (
                      <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Atividades por tipo
            </Text>
          </div>
          <div className="h-64">
            {byTypeChartData.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <Text size="sm" color="muted">
                  Nenhuma atividade registrada ainda.
                </Text>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byTypeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#008B95" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Atividades por mês
            </Text>
            <Text size="sm" color="muted">
              Últimos 6 meses
            </Text>
          </div>
          <div className="h-64">
            {totalAtividades === 0 ? (
              <div className="h-full flex items-center justify-center">
                <Text size="sm" color="muted">
                  Nenhuma atividade registrada ainda.
                </Text>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMonthChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Corretores mais visitados
            </Text>
            <Text size="sm" color="muted">
              Top 10
            </Text>
          </div>
          <div className="h-64 overflow-y-auto">
            {topCorretores.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <Text size="sm" color="muted">
                  Nenhuma atividade registrada ainda.
                </Text>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {topCorretores.map((corretor, index) => (
                  <li
                    key={corretor.name}
                    className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                        {index + 1}
                      </span>
                      <Text size="sm" className="text-gray-900 dark:text-white truncate">
                        {corretor.name}
                      </Text>
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {corretor.count}
                      <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                        {corretor.count === 1 ? 'visita' : 'visitas'}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AtividadesDashboardTab
